// src/rms/components/RmsLayout.js
// ============================================================================
// App shell
// ============================================================================
// Navigation is built from the operator's resolved config, so a storage
// operator gets "Occupancy" and "Access log" while a construction yard gets
// "Dispatch" and "Certifications" — from the same component.
// ============================================================================

import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useRms } from "../context/RmsContext";
import { useAuth } from "../context/AuthContext";
import adEngine from "../ads/adEngine";
import AdRail from "../ads/AdRail";
import AdMasthead from "../ads/AdMasthead";
import LocalePicker from "./LocalePicker";
import useAdSurfaces from "../ads/useAdSurfaces";
import useSectionRotation from "../ads/useSectionRotation";
import { isTransactionalRoute } from "../ads/adInventory";
import Brandmark from "./Brandmark";
import useElementHeightVar from "../hooks/useElementHeightVar";

function RmsLayout({ children }) {
  const { operator, operators, switchOperator, config, t, can, verticalLabel, isContinuous, workflow } = useRms();
  const { signOut, authEnforced } = useAuth();
  const location = useLocation();

  // In-page actions (saves, searches) are frequency-capped. Section changes are
  // not — useSectionRotation below forces a fresh set on every one.
  React.useEffect(() => {
    adEngine.recordAction();
  }, [location.pathname]);

  // Ads are mounted once here rather than per page, so every screen inherits
  // them — and so the transactional exclusion is one decision, not one per
  // screen. Checkout and check-in (and every modal they open: payment,
  // signature, damage inspection) render completely ad-free.
  //
  // Computed before the early return below so the hook order stays stable.
  const showAds =
    !!operator &&
    !!config &&
    !isTransactionalRoute(location.pathname) &&
    operator.ads_enabled &&
    operator.plan_tier !== "ad_free";

  const { mastheads, rail } = useAdSurfaces({ operator, config, enabled: showAds });

  // Fresh advertisers on every move between sections.
  useSectionRotation({ adsEnabled: showAds });

  // The sidebar and the ad rail stick directly beneath this bar, and its
  // height is not a constant: the masthead wraps below 1200px and the bar
  // grows. Measuring it is what keeps an ad from sliding underneath -- see the
  // --rms-topbar-h note in rms.css.
  const topbarRef = useElementHeightVar("--rms-topbar-h");

  if (!operator || !config) return null;

  // Mixed on purpose: some labels are the vertical's own noun and some are
  // fixed features. One t() resolves both -- see RmsContext.
  //
  // The counter screens are hidden from somebody with no operational rights --
  // a bookkeeper, today. Courtesy again: the writes behind them are refused by
  // the RESTRICTIVE policies in 20260820090001, so this only spares them a
  // screen full of buttons that would not work.
  const links = [
    { to: "/rms", label: t("nav.dashboard", "Dashboard"), end: true },
    { to: "/rms/inventory", label: t("fleet", "Inventory") },
    { to: "/rms/reservations", label: t("reservationPlural", "Reservations"), needs: "ops" },
    { to: "/rms/timeline", label: t("nav.timeline", "Timeline") },
    { to: "/rms/availability", label: t("nav.availability", "Availability") },
    { to: "/rms/customers", label: t("customerPlural", "Customers") },
    { to: "/rms/messages", label: t("nav.messages", "Messages") },
  ].filter((l) => !l.needs || can[l.needs]);

  // Continuous-occupancy verticals get an occupancy board and a gate log in
  // place of the trip-oriented screens.
  const specialised = [];
  if (isContinuous) {
    specialised.push({ to: "/rms/occupancy", label: t("nav.occupancy", "Occupancy") });
    if (workflow.accessControlTracking) {
      specialised.push({ to: "/rms/access", label: t("nav.accessLog", "Access log") });
    }
  }
  if (workflow.deliveryEnabled) {
    specialised.push({ to: "/rms/dispatch", label: t("nav.dispatch", "Dispatch") });
  }
  if (workflow.certificationRequired) {
    specialised.push({ to: "/rms/certifications", label: t("nav.certifications", "Certifications") });
  }

  return (
    <div className="rms-root">
      {/* Global header spanning the FULL site width, above the nav/content/rail
          columns. Inside the content column the leaderboard only had ~360px —
          not enough for a 728x90, which is the size affiliate networks ship. */}
      <header className="rms-topbar rms-topbar--global" ref={topbarRef}>
        <div className="rms-topbar-identity">
          {/* A switcher only when there is something to switch to. One
              business should not have to look at a dropdown containing itself
              -- and until this existed, a second business was unreachable
              through the UI entirely, with nothing on screen saying so. */}
          {operators.length > 1 ? (
            <select
              className="rms-select rms-select--sm rms-topbar-business"
              value={operator.id}
              onChange={(e) => switchOperator(e.target.value)}
              aria-label={t("nav.switchBusiness", "Switch business")}
            >
              {operators.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.business_name}
                </option>
              ))}
            </select>
          ) : (
            <div className="rms-topbar-business">{operator.business_name}</div>
          )}
          <div className="rms-topbar-vertical">{verticalLabel}</div>
        </div>

        <AdMasthead selections={mastheads} />

        <div className="rms-topbar-meta">
          <span className={`rms-pill rms-pill--${operator.plan_tier === "ad_free" ? "ok" : "neutral"}`}>
            {operator.plan_tier === "ad_free"
              ? t("plan.adFree", "Ad-Free plan")
              : t("plan.free", "Free plan")}
          </span>
          {/* Full endonym, not a two-letter code. "EN" is only legible to
              someone who already reads English -- exactly the person who does
              not need the control. */}
          <LocalePicker />
          {authEnforced ? (
            <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={signOut}>
              {t("action.signOut", "Sign out")}
            </button>
          ) : null}
        </div>
      </header>

      <div className={`rms-shell${showAds ? " rms-shell--with-rail" : ""}`}>
        <aside className="rms-sidebar">
          <Brandmark />

          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) => `rms-nav-link${isActive ? " is-active" : ""}`}
            >
              {l.label}
            </NavLink>
          ))}

          {specialised.length ? (
            <>
              <div className="rms-nav-section">{verticalLabel}</div>
              {specialised.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) => `rms-nav-link${isActive ? " is-active" : ""}`}
                >
                  {l.label}
                </NavLink>
              ))}
            </>
          ) : null}

          {/* The whole Money section disappears for somebody who cannot use
              any of it -- a heading over nothing is worse than no heading.
              Courtesy only: the pricing tables are guarded by RESTRICTIVE
              policies, so a staff member who navigates here directly gets a
              screen whose saves the database refuses. */}
          {/* All three are visible to everyone now, and all three open on a PIN
              rather than on the session. Hiding them from a staff-level
              terminal would hide them from the owner standing at it, which is
              the one person they are for. Each screen refuses on its own
              terms: rms_elevate for pricing and billing, an absent SELECT
              grant for reports. */}
          <div className="rms-nav-section">{t("nav.sectionMoney", "Money")}</div>
          <NavLink to="/rms/pricing" className={({ isActive }) => `rms-nav-link${isActive ? " is-active" : ""}`}>
            {t("nav.pricing", "Pricing")}
          </NavLink>
          <NavLink to="/rms/billing" className={({ isActive }) => `rms-nav-link${isActive ? " is-active" : ""}`}>
            {t("nav.billing", "Billing")}
          </NavLink>
          <NavLink to="/rms/reports" className={({ isActive }) => `rms-nav-link${isActive ? " is-active" : ""}`}>
            {t("nav.reports", "Reports")}
          </NavLink>

          <div className="rms-nav-section">{t("nav.sectionAccount", "Account")}</div>
          {/* Visible to everyone, because on a shared terminal the session is
              not the person. Whoever taps this meets a PIN prompt, and the PIN
              is what decides -- hiding the link would hide it from the owner
              standing at the counter. */}
          <NavLink to="/rms/settings" className={({ isActive }) => `rms-nav-link${isActive ? " is-active" : ""}`}>
            {t("nav.settings", "Settings")}
          </NavLink>

          {/* Below Settings on purpose: an operator looks for these when
              something has already gone wrong, and the account section is where
              people go when the answer is not on the screen they are on. */}
          <NavLink to="/rms/report-bug" className={({ isActive }) => `rms-nav-link${isActive ? " is-active" : ""}`}>
            {t("nav.reportBug", "Report a bug")}
          </NavLink>
          <NavLink to="/rms/recommend-feature" className={({ isActive }) => `rms-nav-link${isActive ? " is-active" : ""}`}>
            {t("nav.recommendFeature", "Recommend a feature")}
          </NavLink>
        </aside>

        <div className="rms-main">
          <main className="rms-content">{children}</main>
        </div>

        <AdRail stack={rail} />
      </div>
    </div>
  );
}

export default RmsLayout;
